import type { FC, ReactNode } from 'react'
import type { Crumb } from '@/types'
import type { SiteImage } from '@/types/content'

import Image from 'next/image'
import { useRef } from 'react'
import { ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import Breadcrumbs from '@/components/site/breadcrumbs'
import HeroTrust from '@/components/site/hero-trust'
import SplitLetters from '@/components/site/split-letters'
import { labelFor } from '@/components/site/photo'
import { riseDelay } from '@/lib/motion'
import { useMotion } from '@/lib/gsap'
import { WIDE_CONTAINER } from '@/lib/layout'

interface StoryHeroProps {
    titleLines: string[]
    lead: string
    image: SiteImage
    note: string
    statement: string
    crumbs?: Crumb[]
    exteriorOnly?: boolean
    size?: 'display' | 'page'
    imagePosition?: string
    titleId?: string
}

// The draft's opening, shared by the home page and the main service pages:
// a paper heading band, then the photo held still behind the page (fixed,
// clipped to this section) while the statement scrolls up over it, the shade
// deepens and the letters brighten one after another.
// The data-hero-* attributes are also read by the no-flicker CSS in globals.css.
const StoryHero: FC<StoryHeroProps> = ({
    titleLines,
    lead,
    image,
    note,
    statement,
    crumbs,
    exteriorOnly,
    size = 'page',
    imagePosition = 'center 56%',
    titleId = 'page-hero-title'
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useMotion(ref, ({ gsap, desktop }) => {
        gsap.fromTo('[data-hero-shade]', { opacity: desktop ? 0.2 : 0.26 }, {
            opacity: 0.66,
            ease: 'none',
            scrollTrigger: { trigger: '[data-hero-story]', start: 'top 28%', end: 'center 28%', scrub: true }
        })

        gsap.fromTo('[data-hero-heading] [data-letter]', { opacity: 0.3 }, {
            opacity: 1,
            duration: 0.2,
            stagger: { amount: 0.8 },
            ease: 'none',
            scrollTrigger: { trigger: '[data-hero-heading]', start: 'top 78%', end: 'bottom 42%', scrub: true }
        })

        gsap.from('[data-hero-note]', {
            y: 28,
            opacity: 0,
            ease: 'none',
            scrollTrigger: { trigger: '[data-hero-heading]', start: 'top 83%', end: 'top 58%', scrub: true }
        })
    })

    return (
        <section ref={ref} aria-labelledby={titleId} className='relative isolate text-white [clip-path:inset(0)]'>
            <div className='pointer-events-none fixed inset-x-0 top-0 -z-10 h-lvh'>
                <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    priority
                    sizes='100vw'
                    className='hero-zoom object-cover'
                    style={{ objectPosition: imagePosition }}
                />

                <div data-hero-shade aria-hidden='true' className='absolute inset-0 bg-black opacity-50 motion-safe:opacity-[0.26] md:motion-safe:opacity-[0.2]' />

                <span className='absolute bottom-28 left-4 rounded-md bg-[#101820c7] px-2.5 py-0.5 text-xs font-semibold text-white md:bottom-4'>
                    {labelFor(image)}
                </span>
            </div>

            <div className='relative bg-background text-navy'>
                <div className={cn(WIDE_CONTAINER, 'pb-7 pt-5 sm:pb-9 sm:pt-7 lg:pb-12 lg:pt-10')}>
                    {crumbs && crumbs.length > 0 && (
                        <div className='mb-4 lg:mb-7'>
                            <Breadcrumbs items={crumbs} />
                        </div>
                    )}

                    <div className='grid gap-5 sm:gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center lg:gap-16'>
                        <h1
                            id={titleId}
                            className={cn(
                                'font-bold tracking-[-0.035em] text-[#05478a]',
                                size === 'display' ? 'text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.98]' : 'text-[clamp(2.375rem,4.4vw,4.1rem)] leading-[1.02]'
                            )}
                        >
                            {titleLines.map((line, index) => (
                                <span key={line} className='line-mask'>
                                    <span className='line-rise' style={riseDelay(index * 90)}>{line}</span>
                                </span>
                            ))}
                        </h1>

                        <div className='hero-rise w-full lg:max-w-[36rem] lg:justify-self-end' style={riseDelay(220)}>
                            <p className='text-[clamp(1.1875rem,1.4vw,1.375rem)] leading-[1.55] text-[#27455e]'>
                                {lead}
                            </p>

                            {exteriorOnly && (
                                <p className='mt-4 flex items-start gap-2 rounded-xl bg-[#fff4d6] px-4 py-3 text-base font-bold text-navy'>
                                    <ShieldCheck className='mt-0.5 size-5 shrink-0 text-[#a66b00]' aria-hidden='true' />
                                    Exterior windows and doors only. No interior doors or interior windows.
                                </p>
                            )}

                            <HeroTrust className='mt-5' />
                        </div>
                    </div>
                </div>
            </div>

            <div data-hero-story className='relative h-[92svh] min-h-[560px] md:h-[100svh] md:min-h-[760px]'>
                <div className={cn(WIDE_CONTAINER, 'absolute inset-x-0 top-[8svh] grid items-start gap-5 md:top-[16svh] md:grid-cols-[1fr_1.15fr] md:gap-[9vw]')}>
                    <p data-hero-note className='mt-3 max-w-[320px] text-[clamp(1.2rem,1.4vw,1.45rem)] leading-[1.6] text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.45)]'>
                        {note}
                    </p>

                    <p data-hero-heading className='font-heading text-[clamp(1.9rem,3vw,3rem)] font-semibold leading-[1.15] text-white [text-shadow:0_2px_22px_rgba(0,0,0,0.5)]'>
                        <SplitLetters text={statement} />
                    </p>
                </div>
            </div>
        </section>
    )
}

export default StoryHero
